import { isNull, isSingletonStringUnion } from "generic-type-guard";

import { Activity } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { OverviewActivity } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/client-extensions";

import {
	isArrayOf,
	isBoolean,
	isDate,
	isObjectOf,
	isOptionalOf,
	isOr,
	isRecord,
	isString
} from "../../../utilities/typeGuardUtils";

/*
 * id, lastNameName, email can be optional due the reason that we did not persist the data in the past,
 * and we cannot migrate it retrospectively
 */
interface Assignee {
	readonly id?: string;
	readonly firstName: string;
	readonly lastName?: string;
	readonly email?: string;
}

const isAssignee = isObjectOf<Assignee>({
	id: isOptionalOf(isString),
	firstName: isString,
	lastName: isOptionalOf(isString),
	email: isOptionalOf(isString)
});

interface TaskMetadata {
	readonly id: string;
	readonly key: string;
	readonly formKey: string;
	readonly assignee?: Assignee;
}

const isTaskMetadata = isObjectOf<TaskMetadata>({
	id: isString,
	key: isString,
	formKey: isString,
	assignee: isOptionalOf(isObjectOf(isAssignee))
});

interface Entry {
	entry: string;
}

const isEntry = isObjectOf<Entry>({
	entry: isString
});

export interface FachverfahrenMetadata {
	readonly id: string;
	readonly key: string;
	readonly fqn?: string;
	readonly client?: string;
	readonly shortName?: string;
}

const isFachverfahrenMetadata = isObjectOf<FachverfahrenMetadata>({
	id: isString,
	key: isString,
	fqn: isOptionalOf(isString),
	client: isOptionalOf(isString),
	shortName: isOptionalOf(isString)
});

export interface Archival {
	archivalInProgress?: string;
}
const isArchival = isObjectOf<Archival>({
	archivalInProgress: isOptionalOf(isString)
});

export interface Eldorado {
	/**
	 * Aktenkennzeichen von dem Prozess.
	 */
	readonly aktenkennzeichen?: string;

	/**
	 * Mappennummer von dem Prozess.
	 */
	readonly mappenNummer?: string;

	/**
	 * Betreff des Eldorado-Prozesses.
	 */
	readonly betreff?: string;

	/**
	 * Absender des Eldorado-Prozesses.
	 */
	readonly absender?: string;

	/**
	 * Bezug des Eldorado-Prozesses.
	 */
	readonly bezug?: string;

	/**
	 * Dokumenten-Art des Eldorado-Prozesses.
	 */
	readonly dokumentenArt?: string;

	/**
	 * Papierform des Eldorado-Prozesses: 001 für "Elektronisch", 002 für "Hybrid"
	 */
	readonly papierform?: "001" | "002";
}

const isEldorado = isObjectOf<Eldorado>({
	aktenkennzeichen: isOptionalOf(isString),
	mappenNummer: isOptionalOf(isString),
	betreff: isOptionalOf(isString),
	absender: isOptionalOf(isString),
	bezug: isOptionalOf(isString),
	dokumentenArt: isOptionalOf(isString),
	papierform: isOptionalOf(isSingletonStringUnion("001", "002"))
});

/**
 * Beinhaltet wichtige Metadaten für einen Prozess.
 */
export interface ProcessMetadata {
	/**
	 * Name von dem User der den Prozess zuletzt bearbeitet hat.
	 */
	readonly lastChangedByName: string;

	/**
	 * Datum der letzten Änderungen an dem Prozess.
	 */
	readonly lastChangedOn: string | Date;

	/**
	 * Die ID des Prozesses.
	 */
	readonly processId: string;
	/**
	 * Während die processId die ID zu der Prozess-Instanz beschreibt,
	 * beschreibt die processDefinitionId die ID zu der Definition des Prozesses.
	 * Die Prozessdefinition ist üblicherweise ein BPMN-Diagramm.
	 * Eine Prozess-Instanz kann mithilfe der Prozessdefinition gestartet werden.
	 */
	readonly processDefinitionId: string;
	/**
	 * Zeigt an, ob der Prozess sich zurzeit in einem User-Task befindet.
	 * Ein User-Task ist ein Task, welcher mithilfe von menschlicher Interaktion abgeschlossen werden muss.
	 */
	readonly isInUserTask: boolean;
	/**
	 * Zeigt an, ob ein Prozess bereits beendet wurde.
	 */
	readonly isFinished: boolean;

	/**
	 * Vorgangsnummer von dem Prozess.
	 */
	readonly vorgangsnummer?: string;

	/**
	 * Aktenzeichen von dem Prozess.
	 */
	readonly aktenzeichen?: string;

	/**
	 * Metadaten von dem aktuell Task.
	 */
	readonly task: TaskMetadata;
	/**
	 * Metadaten von dem aktuellen Fachverfahren.
	 */
	readonly fachverfahren: FachverfahrenMetadata;
	/**
	 * Beschreibt die Historie der Prozessschritte. Das erste Element (index 0) ist der erste Schritt.
	 */
	readonly fachverfahrenHistory?: Entry[];

	/**
	 * Flag das auf true gesetzt wird, wenn Antragsdaten automatisch eingelesen wurden (Mapping oder interner Antrag)
	 */
	readonly createdAutomatically: boolean;

	/**
	 * Wenn eine ELDORADO-Veraktung läuft, steht hier die UUID des Veraktungsvorgangs. Ansonsten leer.
	 */
	readonly archival?: Archival;

	/**
	 * ELDORADO-Metadaten
	 */
	readonly eldorado?: Eldorado;

	/**
	 * Fristen (deadliens)
	 */
	readonly fristen?: Fristen;

	/**
	 * Links (Verknüpfungen, contains only the source process reference)
	 */
	readonly links?: Links;

	/**
	 * Daten des angemeldeten Nutzers zum Zeitpunkt der Dokumentgenerierung.
	 */
	readonly angemeldetePerson?: AngemeldetePerson;
}

const isLinkedProcessInfo = isObjectOf<LinkedProcessInfo>({
	docRef: isString,
	vorgangsnummer: isOr(isOptionalOf(isString), isNull),
	aktenzeichen: isOr(isOptionalOf(isString), isNull),
	fvShortName: isOptionalOf(isString),
	fvKey: isOptionalOf(isString)
});

export interface LinkedProcessInfo {
	readonly docRef: string;
	readonly vorgangsnummer?: string | null;
	readonly aktenzeichen?: string | null;
	readonly fvShortName: string;
	readonly fvKey: string;
}

const isLinks = isObjectOf<Links>({
	sourceProcessId: isOptionalOf(isString),
	linkedProcesses: isOptionalOf(isArrayOf(isLinkedProcessInfo))
});

export interface AngemeldetePerson {
	readonly fullName?: string;
	readonly firstName?: string;
	readonly lastName?: string;
	readonly email?: string;
}

const isAngemeldetePerson = isObjectOf<AngemeldetePerson>({
	fullName: isOptionalOf(isString),
	firstName: isOptionalOf(isString),
	lastName: isOptionalOf(isString),
	email: isOptionalOf(isString)
});

export interface Links {
	readonly sourceProcessId?: string;
	readonly linkedProcesses?: LinkedProcessInfo[];
}

const isFristen = isObjectOf<Fristen>({
	allFristenErfuellt: isOptionalOf(isBoolean)
});

export interface Fristen {
	/**
	 * Whether or not all deadlines have been marked "fulfilled"
	 */
	readonly allFristenErfuellt?: boolean;
}

const isProcessMetadata = isObjectOf<ProcessMetadata>({
	lastChangedByName: isString,
	lastChangedOn: isOr(isString, isDate),
	processId: isString,
	processDefinitionId: isString,
	isInUserTask: isBoolean,
	isFinished: isBoolean,
	vorgangsnummer: isOptionalOf(isString),
	aktenzeichen: isOptionalOf(isString),
	task: isTaskMetadata,
	fachverfahren: isFachverfahrenMetadata,
	fachverfahrenHistory: isOptionalOf(isArrayOf(isEntry)),
	createdAutomatically: isOptionalOf(isBoolean),
	archival: isOptionalOf(isArchival),
	eldorado: isOptionalOf(isEldorado),
	fristen: isOptionalOf(isFristen),
	links: isOptionalOf(isLinks),
	angemeldetePerson: isOptionalOf(isAngemeldetePerson)
});

export interface ProcessMetadataContainingDocument extends Activity.Data.Document {
	readonly root: {
		readonly processMetadata: ProcessMetadata;
	};
}

export const isDocumentWithIdAndProcessMetadata = isObjectOf<ProcessMetadataContainingDocument>({
	id: isString,
	root: isObjectOf({ processMetadata: isProcessMetadata })
});

export const isDocumentWithProcessMetadata = isObjectOf<ProcessMetadataContainingDocument>({
	root: isObjectOf({ processMetadata: isProcessMetadata })
});

export interface ProcessOverviewData extends OverviewActivity.Data.DocumentListData {
	readonly documents: ProcessMetadataContainingDocument[];
	readonly totalDocumentsCount: number;
}

export function isProcessOverviewData(obj: unknown): obj is ProcessOverviewData {
	return (
		isRecord(obj) &&
		typeof obj.totalDocumentsCount === "number" &&
		isArrayOf(isDocumentWithProcessMetadata)(obj.documents)
	);
}
